// ============================================================
// store/uiStore.js
// EV Powertrain Simulator — UI 전역 상태 (탭, 테마, 모달, 언어)
// 패턴: 팩토리(Factory) — createUIStore() 호출로 인스턴스 생성
// 사용법: import { createUIStore } from '../store/uiStore.js';
// Phase 3: Zustand 상태 관리 도입
// ============================================================

/**
 * uiStore — 탭 선택, 언어, 테마, 모달 open/close, GraphInfo 팝업
 *
 * LocalStorage persist 항목:
 *   - lang    : 'ko' | 'en'
 *   - isDark  : boolean
 *   - activeTab, activeCat
 */

// [설계 의도] UI 상태 중 lang·isDark만 영속화하는 이유:
//   탭 선택(activeTab)은 URL 라우팅이 없는 SPA이므로 새로고침 시 기본 탭으로 복귀하는 것이 자연스럽다.
//   모달 플래그는 세션 메모리에만 보관해 새로고침 후 모달이 열려있는 비정상 UX를 방지한다.
const UI_STORAGE_KEY = 'ev-sim-ui-v1';

/**
 * localStorage에서 UI 설정 스냅샷을 읽어 반환한다.
 *
 * JSON 파싱 실패나 localStorage 미지원 환경에서는 null을 반환하고
 * 호출부가 기본값으로 폴백하도록 한다.
 *
 * @returns {{ lang: string, isDark: boolean }|null}
 */
function loadUIFromStorage() {
  try {
    const raw = localStorage.getItem(UI_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

/**
 * 현재 UI 상태 중 영속화 대상(lang, isDark)만 localStorage에 저장한다.
 *
 * 탭 상태·모달 플래그 등은 직렬화 대상에서 제외해 저장 용량을 최소화한다.
 * 저장 실패(용량 초과, SSR 환경)는 조용히 무시한다.
 *
 * @param {{ lang: string, isDark: boolean }} s  저장할 UI 상태 객체
 */
function saveUIToStorage(s) {
  try {
    localStorage.setItem(UI_STORAGE_KEY, JSON.stringify({
      lang: s.lang, isDark: s.isDark,
    }));
  } catch { /* ignore */ }
}

/**
 * UI 전역 상태 스토어 인스턴스를 생성한다.
 *
 * 초기 테마는 localStorage 저장값을 우선 적용하고,
 * 최초 방문 시에는 OS의 prefers-color-scheme 미디어 쿼리로 결정한다.
 * 이후 사용자 선택은 localStorage에 영속화되어 재방문 시 복원된다.
 *
 * 모달 플래그(showXxxModal)는 open/close 쌍으로 관리되며 영속화하지 않는다.
 * Control Modal은 group·subTab 파라미터로 초기 포커스를 지정할 수 있다.
 *
 * @returns {object}  스토어 인스턴스 (getState, subscribe, 액션 메서드 포함)
 */
function createUIStore() {
  const saved = loadUIFromStorage();

  // OS 설정 기반 초기값 (최초 방문 시에만, 이후는 사용자 선택 유지)
  const osPrefersDark = typeof window !== 'undefined'
    ? window.matchMedia('(prefers-color-scheme: dark)').matches
    : false;

  let state = {
    // ── 언어 / 테마 ───────────────────────────────
    lang:    saved?.lang   ?? 'ko',
    isDark:  saved?.isDark ?? osPrefersDark,  // 최초 방문 시만 OS 설정 적용

    // ── 탭 내비게이션 ─────────────────────────────
    activeTab:  'launch',    // 시동 시 발진 탭으로 시작
    activeCat:  'sim',

    // ── 모달 플래그 ───────────────────────────────
    showVehicleModal:    false,
    showEnvModal:        false,
    showControlModal:    false,
    showCSVModal:        false,
    showDataExportPopup: false,
    showVehiclePresetModal: false,

    // ── 모달 초기 포커스 (Control Modal) ─────────
    ctrlInitGroup:  null,   // 'drive' | 'bms' | ...
    ctrlInitSubTab: null,   // 'sig_input' | ...

    // ── GraphInfo 팝업 ────────────────────────────
    graphInfoPopup: null,   // { info, title } | null

    // ── 액션: 언어 / 테마 ─────────────────────────

    /**
     * 언어를 직접 설정하고 localStorage에 저장한다.
     * @param {'ko'|'en'} v  설정할 언어 코드
     */
    setLang(v) {
      state.lang = v;
      saveUIToStorage(state);
      state._notify();
    },

    /**
     * 언어를 한국어('ko')와 영어('en') 사이에서 토글한다.
     * 변경 후 localStorage에 즉시 영속화한다.
     */
    toggleLang() {
      state.lang = state.lang === 'ko' ? 'en' : 'ko';
      saveUIToStorage(state);
      state._notify();
    },

    /**
     * 다크모드 여부를 직접 설정하고 localStorage에 저장한다.
     * @param {boolean} v  true: 다크모드, false: 라이트모드
     */
    setIsDark(v) {
      state.isDark = v;
      saveUIToStorage(state);
      state._notify();
    },

    /**
     * 다크/라이트 모드를 토글하고 localStorage에 영속화한다.
     */
    toggleDark() {
      state.isDark = !state.isDark;
      saveUIToStorage(state);
      state._notify();
    },

    // ── 액션: 탭 ──────────────────────────────────

    /**
     * 활성 탭을 변경한다.
     * @param {string} tab  탭 ID (예: 'launch', 'rl', 'energy')
     */
    setActiveTab(tab) {
      state.activeTab = tab;
      saveUIToStorage(state);
      state._notify();
    },

    /**
     * 활성 카테고리를 변경한다.
     * @param {string} cat  카테고리 ID (예: 'sim', 'safety', 'analysis')
     */
    setActiveCat(cat) {
      state.activeCat = cat;
      saveUIToStorage(state);
      state._notify();
    },

    // ── 액션: 모달 ────────────────────────────────
    /** 차량 선택 모달을 연다. */
    openVehicleModal()    { state.showVehicleModal    = true;  state._notify(); },
    /** 차량 선택 모달을 닫는다. */
    closeVehicleModal()   { state.showVehicleModal    = false; state._notify(); },
    /** 환경 설정 모달을 연다. */
    openEnvModal()        { state.showEnvModal        = true;  state._notify(); },
    /** 환경 설정 모달을 닫는다. */
    closeEnvModal()       { state.showEnvModal        = false; state._notify(); },
    /** CSV 업로드 모달을 연다. */
    openCSVModal()        { state.showCSVModal        = true;  state._notify(); },
    /** CSV 업로드 모달을 닫는다. */
    closeCSVModal()       { state.showCSVModal        = false; state._notify(); },
    /** 데이터 내보내기 팝업을 연다. */
    openDataExportPopup() { state.showDataExportPopup = true;  state._notify(); },
    /** 데이터 내보내기 팝업을 닫는다. */
    closeDataExportPopup(){ state.showDataExportPopup = false; state._notify(); },
    /** 차량 프리셋 관리 모달을 연다. */
    openVehiclePresetModal()  { state.showVehiclePresetModal = true;  state._notify(); },
    /** 차량 프리셋 관리 모달을 닫는다. */
    closeVehiclePresetModal() { state.showVehiclePresetModal = false; state._notify(); },

    /**
     * 제어 파라미터 모달을 열고 초기 포커스 그룹/서브탭을 설정한다.
     *
     * 모달이 열릴 때 특정 파라미터 그룹(예: 'drive', 'bms')이나
     * 서브탭(예: 'sig_input')으로 자동 스크롤하기 위해 사용한다.
     *
     * @param {string|null} [group=null]   초기 포커스 그룹 ID
     * @param {string|null} [subTab=null]  초기 포커스 서브탭 ID
     */
    openControlModal(group = null, subTab = null) {
      state.showControlModal = true;
      state.ctrlInitGroup    = group;
      state.ctrlInitSubTab   = subTab;
      state._notify();
    },

    /**
     * 제어 파라미터 모달을 닫고 초기 포커스 설정을 초기화한다.
     */
    closeControlModal() {
      state.showControlModal = false;
      state.ctrlInitGroup    = null;
      state.ctrlInitSubTab   = null;
      state._notify();
    },

    // ── 액션: GraphInfo 팝업 ──────────────────────

    /**
     * 그래프 정보 팝업을 열고 표시할 내용을 설정한다.
     *
     * 그래프 우측 상단의 ⓘ 아이콘 클릭 시 호출되며 해당 그래프의
     * 물리적 의미, 수식, 단위 설명을 팝업으로 표시한다.
     *
     * @param {string} info   팝업 본문 (마크다운 또는 HTML 문자열)
     * @param {string} title  팝업 제목
     */
    openGraphInfo(info, title) {
      state.graphInfoPopup = { info, title };
      state._notify();
    },

    /**
     * 그래프 정보 팝업을 닫고 graphInfoPopup 상태를 null로 초기화한다.
     */
    closeGraphInfo() {
      state.graphInfoPopup = null;
      state._notify();
    },

    // ── 내부 ──────────────────────────────────────
    _listeners: new Set(),

    /** 등록된 모든 구독자에게 현재 상태를 동기 전달한다. */
    _notify() { state._listeners.forEach(fn => fn(state)); },

    /**
     * 상태 변경 구독을 등록하고, 해제 함수를 반환한다.
     * @param {(state: object) => void} fn  상태 변경 콜백
     * @returns {() => void}                구독 해제 함수
     */
    subscribe(fn) {
      state._listeners.add(fn);
      return () => state._listeners.delete(fn);
    },

    /**
     * 현재 스토어 상태 스냅샷을 반환한다.
     * @returns {object}
     */
    getState() { return state; },
  };

  return state;
}

export { createUIStore };